import React, { useState, useMemo, useCallback, memo, useEffect } from "react";
import { useIntl } from "react-intl";
import {
  ChannelAccount,
  Company,
  Entity,
  ProvisionalBody,
  ProvisionalPost,
  PublicationHosting,
} from "../../types";
import {
  ChannelAccountsSelector,
  ChannelAccountsUiElement,
} from "../ChannelAccountBodyComponents";
import { getCSRFToken } from "../utils/various";
import Tooltip from "../Tooltip";
import PublicationManager from "./PublicationManager";

import { HiddenInput } from "../DynamicFormGenerator";
import BodyEditor from "../BodyEditor";
import {
  PaperAirplaneIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/20/solid";
import { InboxArrowDownIcon } from "@heroicons/react/20/solid";
import usePubValidation from "../usePubValidation";
import ErrorDisplayer from "../ErrorDisplayer";
import { XMarkIcon } from "@heroicons/react/24/outline";
import MentionSelector from "./mentions/MentionSelector";

import CustomPopover from "../Popover";
import Flatpickr from "react-flatpickr";
import { getBodyCharacterLimit } from "../utils/publisher/constraints";

type Props = {
  bodies: ProvisionalBody[];
  post: ProvisionalPost;
  channel_accounts: (ChannelAccount & { id: number })[];
  variables: Record<string, string | null>;
  ai_image_enabled?: boolean;
  isStephanePlazaAccount: boolean;
  industry: string | null;
  company: Company;
  hosting?: PublicationHosting;
};


// Les autres versions qui pointent la même composition (visuel partagé, plan §6.8) — le
// serveur ne peut pas le savoir pendant la session (brouillons orphelins), seul le state le sait.
const sharedCompositionCount = (
  bodies: Record<number, ProvisionalBody>,
  body: ProvisionalBody,
) =>
  body?.composition_id
    ? Object.values(bodies).filter(
        (b) => b.composition_id === body.composition_id && b !== body,
      ).length
    : 0;

const PostStateManager = (props: Props) => {
  const intl = useIntl();

  // ### Tabs
  const mobileTabs = [
    { label: intl.formatMessage({ id: "publisher.content_tab", defaultMessage: "Contenu" }), value: "content" },
    { label: intl.formatMessage({ id: "publisher.publication_tab", defaultMessage: "Publication" }), value: "publication" },
  ];
  const [activeMobileTab, setActiveMobileTab] = useState("content");

  const hChannelAccounts = useMemo(
    () =>
      Object.fromEntries(
        props.channel_accounts.map((ca) => [ca.id as number, ca])
      ),
    [props.channel_accounts]
  );

  const [hBodies, setHBodies] = useState<Record<number, ProvisionalBody>>(
    Object.fromEntries(props.bodies.map((b) => [b.provisional_id, b]))
  );

  const [showModalForBodyProvId, setShowModalForBodyProvId] = useState<
    null | number
  >(
    props.post.post_on_channels_attributes.length > 0
      ? null
      : parseInt(Object.keys(hBodies)[0])
  );

  // Post
  const [post, setPost] = useState<ProvisionalPost>(props.post);

  // Le body dont la vidéo est hébergée (une seule par post), envoyé à côté des POCs.
  const [hostedBodyPid, setHostedBodyPid] = useState<number | null>(
    props.hosting?.body_provisional_id ?? null
  );

  /* CHARACTER LIMIT */
  const characterLimitByBody = useMemo(
    () =>
      Object.fromEntries(
        props.post.post_on_channels_attributes
          .map((poc) => [
            poc.body_provisional_id,
            getBodyCharacterLimit(poc.body_provisional_id, props.post.post_on_channels_attributes, hChannelAccounts),
          ])
      ),
    [props.post.post_on_channels_attributes, hChannelAccounts]
  );

  /* MENTIONS */
  const [mentions, setMentions] = useState<
    Record<string, Record<string, Entity>>
  >({});
  const [showMentionSelector, setShowMentionSelector] = useState(false);

  const memoMentionSelectorRenderer = useMemo(
    () => (body: ProvisionalBody, insert: any) => {
      // On cherche tous les pocs qui ont ce body
      const pocsForThisBody = post.post_on_channels_attributes.filter(
        (poc) => poc.body_provisional_id === body.provisional_id
      );

      // On regarde si au moins un poc contient un channel_account identifier linkedin
      const bodyHasLinkedinCA = props.channel_accounts
        .filter((ca) => {
          return pocsForThisBody.some(
            (poc) => poc.channel_account_id === ca.id
          );
        })
        ?.map((ca) => ca.channel.identifier)
        ?.includes("linkedin");

      return (
        <MentionSelector
          post={post}
          channelAccounts={props.channel_accounts}
          insert={insert}
          showSelector={bodyHasLinkedinCA}
          setGlobalMentions={setMentions}
        />
      );
    },
    [post, hChannelAccounts, showMentionSelector]
  );
  /* END MENTIONS */

  // On garde en mémoire les pocs originaux pour l'édition
  const originalPocsByCa = useMemo(
    () =>
      Object.fromEntries(
        props.post.post_on_channels_attributes
          .filter((p) => p.id)
          .map((p) => [p.channel_account_id, p.id])
      ),
    []
  ) as Record<number, number>;

  // Source sync — Les posts liés à un Product::Item (annonce) sont automatiquement
  // mis à jour quand l'annonce change (refresh_uncommited_posts). Dès que l'utilisateur
  // modifie le contenu (texte, lien, composition) via le BodyEditor, on marque le post comme
  // désynchronisé (synced_with_source: false) pour empêcher l'écrasement de ses modifications.
  const markAsDesynced = useCallback(() => {
    setPost((prev) => {
      if (!prev.synced_with_source) return prev;
      return { ...prev, synced_with_source: false };
    });
  }, []);

  const desyncedSetHBodies = useCallback<typeof setHBodies>((action) => {
    markAsDesynced();
    setHBodies(action);
  }, [markAsDesynced]);

  const switchBodyPocs = (
    targetIds: number[],
    bodyId: ProvisionalBody["provisional_id"]
  ) => {
    const finalPocs: ProvisionalPost["post_on_channels_attributes"][0][] = [];
    const remainsTargetIds = new Set(targetIds);
    post.post_on_channels_attributes.forEach((poc) => {
      // Si le channel_account_id est dans les targetIds,
      // On associe ce POC à ce bodyId
      if (remainsTargetIds.has(poc.channel_account_id)) {
        finalPocs.push({ ...poc, body_provisional_id: bodyId });
      } else {
        // Si le channel_account_id n'est pas dans les targetIds :
        // Soit le POC n'avait pas ce bodyId => on le laisse tranquille et on le garde
        // Soit le POC avait ce bodyId => on supprime le POC
        if (poc.body_provisional_id !== bodyId) finalPocs.push(poc);
      }
      remainsTargetIds.delete(poc.channel_account_id);
    });
    // Si des targetsIds restent, on les rajoute
    remainsTargetIds.forEach((ca_id) =>
      finalPocs.push({
        body_provisional_id: bodyId,
        forced_scheduled_at: post.scheduled_at,
        channel_account_id: ca_id,
        ...defaultValuesForChannel(ca_id),
      })
    );
    setPost((prev) => ({ ...prev, post_on_channels_attributes: finalPocs }));

    // On supprime les bodies sans pocs
    const pocsBPid = finalPocs?.map((poc) => poc.body_provisional_id);
    setHBodies((hBodies) => {
      const nextBodies = Object.fromEntries(
        Object.entries(hBodies).filter(([pid, _]) =>
          pocsBPid.includes(parseInt(pid))
        )
      );
      return nextBodies;
    });
  };

  // fonction de gestion d'erreur
  const createNewBodyForPocGroup = (
    pocs: ProvisionalPost["post_on_channels_attributes"],
    error: string
  ) => {
    const constraint =
      hChannelAccounts[pocs[0]?.channel_account_id]?.channel_constraints;
    if (!constraint) return;

    // La correction de contrainte est une divergence par nature : le body corrigé reçoit son
    // propre fork de la compo, jamais le visuel partagé de son origine. Il garde le
    // composition_id d'origine le temps que le fork arrive.
    const { id, ...body } = hBodies[pocs[0].body_provisional_id];
    const { composition_id } = body;

    let newBodyPid: number | null = null;

    let message = body.message || "";
    let link_url = body.link_url;

    if (error == "character_limit") {
      message = message.substring(0, constraint[error] || 0);
    }
    if (error == "invalid_link") {
      link_url = "";
    }

    setHBodies((prev) => {
      newBodyPid =
        Math.max(...Object.keys(prev).map((k) => parseInt(k)), 0) + 1;
      return {
        ...prev,
        [newBodyPid]: {
          ...body,
          message,
          link_url,
          provisional_id: newBodyPid,
        },
      };
    });
    document
      ?.getElementById("main-container")
      ?.scroll({ top: 0, behavior: "smooth" });
    setTimeout(async () => {
      if (newBodyPid) {
        switchBodyPocs(
          pocs.map((p) => p.channel_account_id),
          newBodyPid
        );
        setDesktopActiveBody(newBodyPid);
      }
      // Le body corrigé reçoit SON fork de compo : ses retouches ne touchent pas l'original.
      if (!composition_id || !newBodyPid) return;

      const response = await fetch(`/compositions/${composition_id}/fork`, {
        method: "POST",
        headers: { "X-CSRF-Token": getCSRFToken() },
      });
      if (!response.ok) return;

      const { id: forkId } = await response.json();
      const pid = newBodyPid;
      setHBodies((prev) =>
        prev[pid] ? { ...prev, [pid]: { ...prev[pid], composition_id: forkId } } : prev
      );
    }, 500);
  };

  const deletePocGroup = (
    pocs: ProvisionalPost["post_on_channels_attributes"]
  ) => {
    const ids = pocs.map((p) => p.channel_account_id);

    document
      ?.getElementById("main-container")
      ?.scroll({ top: 0, behavior: "smooth" });

    let newBodyPids: number[] = []; // store surviving bodies
    setPost((prev) => {
      const post_on_channels_attributes =
        prev.post_on_channels_attributes.filter(
          (p) => !ids.includes(p.channel_account_id)
        );
      newBodyPids = post_on_channels_attributes.map(
        (p) => p.body_provisional_id
      );

      return { ...prev, post_on_channels_attributes };
    });

    setTimeout(() => {
      // on nettoie les bodies
      Object.entries(hBodies).forEach(([pid, body]) => {
        const iPid = parseInt(pid);
        if (!newBodyPids.includes(iPid)) {
          destroyBody(iPid);
        }
      });
    }, 50);
  };

  // les bodies dans l'ordre pour itérer
  const sortedBodyKeys = useMemo(() => {
    return Object.keys(hBodies)
      .map((p) => parseInt(p))
      .sort();
  }, [Object.keys(hBodies).length]); // le nombre de body a changé

  // Callback avant la suppression d'un body
  const onBodyRemoval = useMemo(
    () => (bodyPid: number) => {
      const post_on_channels_attributes =
        post.post_on_channels_attributes.filter(
          (poc) => poc.body_provisional_id !== bodyPid
        );
      // On ouvre l'onglet du dernier body
      const lastBodyId = Object.keys(hBodies)
        .filter((bId) => parseInt(bId) !== bodyPid)
        .at(-1) as string;
      setDesktopActiveBody(parseInt(lastBodyId));
      setPost((post) => ({ ...post, post_on_channels_attributes }));
    },
    [post]
  );

  // Pour la suppression d'un body vide
  const destroyBody = useCallback((bodyPid: number) => {
    setHBodies(({ [bodyPid]: _, ...keep }) => {
      const lastBodyId = Object.keys(keep)
        .filter((bId) => parseInt(bId) !== bodyPid)
        .at(-1) as string;
      setDesktopActiveBody(parseInt(lastBodyId));
      return keep;
    });
  }, []);

  // Callback après la duplication d'un body
  const onBodyDuplication = useCallback(
    (bodyPid: number) => {
      setShowModalForBodyProvId(bodyPid);
      setDesktopActiveBody(bodyPid);
    },
    [hBodies]
  );

  // Which body is active on desktop
  const [desktopActiveBody, setDesktopActiveBody] = useState(sortedBodyKeys[0]);
  const isActive = (bPid: number) => bPid === desktopActiveBody;

  const defaultValuesForChannel = (channelAccountId: number) => {
    const channelAccount = hChannelAccounts[channelAccountId];
    const ChannelIdentifier = channelAccount?.channel?.identifier;
    return {
      ...(ChannelIdentifier === "tiktok" && {
        channel_specific_data: {
          disable_comments: true,
          disable_duet: true,
          disable_stitch: true,
          privacy_level: null,
          ...(Object.keys(mentions).length > 0 && {
            mentions: mentions[ChannelIdentifier],
          }),
        },
      }),
      ...(ChannelIdentifier === "instagram" && {
        channel_specific_data: {
          collaborators: [],
          first_comment: "",
          dm_automation: { enabled: false, message: "", response: "" },
          ...(Object.keys(mentions).length > 0 && {
            mentions: mentions[ChannelIdentifier],
            cover_url: [],
          }),
        },
      }),
      ...(ChannelIdentifier === "youtube" && {
        channel_specific_data: {
          video_title: "",
          privacy_status: "public",
        },
      }),
      ...(ChannelIdentifier === "facebook" && {
        channel_specific_data: {
          ...(channelAccount?.missing_scopes_for_comment_automation?.length === 0 && { first_comment: "" }),
            dm_automation: { enabled: false, message: "", response: "" },
          ...(Object.keys(mentions).length > 0 && {
            mentions: mentions[ChannelIdentifier],
          }),
        },
      }),
      ...(!["tiktok", "instagram", "youtube", "facebook"].includes(ChannelIdentifier) && {
        channel_specific_data: {
          ...(Object.keys(mentions).length > 0 && {
            mentions: mentions[ChannelIdentifier],
          }),
        },
      }),
    };
  };

  // Prevent recreating the selector component on renders
  const memoizedSelectorRenderer = useMemo(
    () => (bPid: ProvisionalBody["provisional_id"], buttonUI: JSX.Element) => {
      return (
        <ChannelAccountsSelector
          bPid={bPid}
          post={post}
          destroyEmptyBody={destroyBody}
          hChannelAccounts={hChannelAccounts}
          switchBodyPocs={switchBodyPocs}
          showModalForBodyProvId={showModalForBodyProvId}
          setShowModalForBodyProvId={setShowModalForBodyProvId}
          buttonUI={buttonUI}
          hosting={
            props.hosting?.available
              ? { ...props.hosting, hostedBodyPid, setHostedBodyPid, video: !!hBodies[bPid]?.video }
              : undefined
          }
        />
      );
    },
    [post, hChannelAccounts, showModalForBodyProvId, hostedBodyPid, hBodies]
  );

  const [openModalForPoc, setOpenModalForPoc] = useState<
    ProvisionalPost["post_on_channel_attributes"][0] | null
  >(null);

  const validationObject = usePubValidation({
    pocs: post.post_on_channels_attributes,
    hChannelAccounts,
    hBodies,
    instantPublish: post.instant_publish,
    setOpenModalForPoc,
    deleteGroup: deletePocGroup,
    createNewBodyForGroup: createNewBodyForPocGroup,
  });

  const [showInfoMessage, setShowInfoMessage] = useState(true);

  useEffect(() => {
    if (showInfoMessage) {
      const timer = setTimeout(() => {
        setShowInfoMessage(false);
      }, 30000); // 30 seconds

      return () => clearTimeout(timer);
    }
  }, [showInfoMessage]);

  const submit = (e: any, opt?: { draft: boolean }) => {
    const draft = opt?.draft;
    const status = draft ? "draft" : "scheduled";
    e.target.style.setProperty("pointer-events", "none"); // Prevent double clicks when waiting for render
    setPost((prev) => ({
      ...prev,
      draft,
      post_on_channels_attributes: prev.post_on_channels_attributes.map(
        (p) => ({ ...p, status })
      ),
    }));

    // Delay for rendering of inputs
    setTimeout(() => {
      const form = document
        .getElementById("post_state_manager")
        ?.closest("form");
      if (form) {
        form.submit();
        e.target.style.setProperty("pointer-events", "auto");
      }
    }, 0);
  };

  const togglePublish = (instantPublish: boolean) => {
    setPost((prev) => {
      return {
        ...prev,
        scheduled_at: !instantPublish
          ? prev.scheduled_at
          : new Date().toISOString(),
        instant_publish: instantPublish,
        post_on_channels_attributes: prev.post_on_channels_attributes.map(
          (poc) => ({
            ...poc,
            forced_scheduled_at: instantPublish
              ? ""
              : poc.forced_scheduled_at || new Date().toISOString(),
          })
        ),
      };
    });
  };

  const updatePostDatetime = (stringifiedDate: string) => {
    setPost((prev) => ({
      ...prev,
      scheduled_at: stringifiedDate,
      post_on_channels_attributes: prev.post_on_channels_attributes.map(
        (poc) => {
          return { ...poc, forced_scheduled_at: stringifiedDate };
        }
      ),
    }));
  };

  // Le post est issu d'une proposition, on ne peut pas changer la date
  const disabledDate = post["was_proposal?"];

  return (
    <div id="post_state_manager" className="w-full flex flex-col gap-5">
      <div className="flex justify-between gap-3 mx-1 sm:mx-0">
        <h1 className="flex items-start text-lg font-medium text-gray-700 whitespace-nowrap">
          {intl.formatMessage({ id: "publisher.create_publication", defaultMessage: "Créer une publication" })}
        </h1>
        <div className="flex flex-wrap gap-3 w-full justify-end">
          {(post.draft || !post.id) && (
            <Tooltip text={intl.formatMessage({ id: "publisher.save_as_draft", defaultMessage: "Enregistrer en brouillon" })}>
              <button
                type="button"
                onClick={(e) => submit(e, { draft: true })}
                className="clickable-text whitespace-nowrap"
              >
                {intl.formatMessage({ id: "publisher.save", defaultMessage: "Sauvegarder" })}
              </button>
            </Tooltip>
          )}
          <CustomPopover
            button={
              <div className="flex h-9 text-white font-medium text-sm rounded-md items-center bg-brand_main">
                <p className="pr-2 pl-3">{intl.formatMessage({ id: "publisher.publish", defaultMessage: "Publier" })}</p>
                <hr />
                <div className="bg-brand_main border-l border-brand_darker bord h-full item-center rounded-r-md flex items-center hover:bg-brand_darker px-2 duration-200 ">
                  <ChevronDownIcon className="size-6 font-medium" />
                </div>
              </div>
            }
            position="bottom-end"
            panelClasses="drop-shadow-sm"
            disabled={false}
          >
            <div className="bg-white rounded-md shadow-md flex flex-col w-80 sm:w-96 gap-3 p-5 items-end">
              {disabledDate && (
                <p className="text-gray-500">
                  <ExclamationTriangleIcon className="inline size-5" />
                  {intl.formatMessage({ id: "publisher.proposal_locked", defaultMessage: "Ce post est issu d'une proposition, sa date ne peut pas être modifiée." })}
                </p>
              )}
              <div className="flex flex-col">
                <label className="rounded-tl-md rounded-tr-md relative flex gap-1 cursor-pointer border border-gray-200 p-4 focus:outline-none has-[:checked]:border-brand_lighter has-[:checked]:bg-brand_lighter/30">
                  <input
                    type="radio"
                    className="m-1 border-brand_main text-brand_main focus:ring-brand_main"
                    disabled={disabledDate}
                    checked={post.instant_publish}
                    aria-label="publier maintenant"
                    aria-description="publier Maintenant"
                    onChange={(e) => {
                      togglePublish(true);
                    }}
                  />
                  <div className="flex flex-col">
                    <span className="flex items-center font-medium text-gray-900">
                      {intl.formatMessage({ id: "publisher.publish_now", defaultMessage: "Publier maintenant" })}
                    </span>
                    <span className="block text-gray-500">
                      {intl.formatMessage({ id: "publisher.publish_now_desc", defaultMessage: "Publier le contenu immédiatement sur les réseaux sélectionnés" })}
                    </span>
                  </div>
                </label>
                <label className="max-w-screen w-full rounded-bl-md rounded-br-md relative flex gap-1 cursor-pointer border p-4 focus:outline-none has-[:checked]:border-brand_lighter has-[:checked]:bg-brand_lighter/30">
                  <input
                    type="radio"
                    disabled={disabledDate}
                    className="m-1 border-brand_main text-brand_main focus:ring-brand_main"
                    checked={!post.instant_publish}
                    aria-label="publier maintenant"
                    aria-description="publier Maintenant"
                    onChange={(e) => togglePublish(false)}
                  />
                  <div className="flex flex-col w-full gap-3">
                    <div className="flex flex-col">
                      <span className="flex items-center font-medium text-gray-900">
                        {intl.formatMessage({ id: "publisher.schedule", defaultMessage: "Programmer" })}
                      </span>
                      <span className="block text-gray-500">
                        {intl.formatMessage({ id: "publisher.schedule_desc", defaultMessage: "Choisir le moment de la publication" })}
                      </span>
                    </div>
                    {!post.instant_publish && (
                      <div className="flex flex-wrap justify-between w-full gap-6">
                        <div className="flex-1">
                          <label
                            className="font-medium text-brand_darker"
                            htmlFor="datetime-post-scheduled-at"
                          >
                            {intl.formatMessage({ id: "publisher.publication_date", defaultMessage: "Date de publication" })}
                          </label>
                          <Flatpickr
                            defaultValue={
                              post.scheduled_at
                                ? new Date(post.scheduled_at)?.toLocaleString(
                                  intl.locale
                                )
                                : new Date().toISOString()
                            }
                            id="datetime-post-scheduled-at"
                            name="scheduled_at"
                            onChange={(dateArray: Date[]) =>
                              updatePostDatetime(dateArray[0].toISOString())
                            }
                            disabled={post.instant_publish || disabledDate}
                            className={`min-w-56 custom-form-input flex flex-1 rounded-md border-gray-300 shadow-sm cursor-pointer`}
                            options={{
                              enableTime: true,
                              time_24hr: true,
                              enableSeconds: true,
                              dateFormat: "d-m-Y H:i:S",
                              minDate: new Date(),
                              disableMobile: true,
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </label>
              </div>
              <button
                type="button"
                onClick={submit}
                className={`btn-brand-primary w-fit gap-2`}
              >
                <PaperAirplaneIcon className="size-5" />
                {intl.formatMessage({ id: "publisher.validate", defaultMessage: "Valider" })}
              </button>
            </div>
          </CustomPopover>
        </div>
      </div>
      <ul className="flex gap-5 border-b lg:hidden">
        {mobileTabs.map((tab) => (
          <li
            key={tab.value}
            className={`text-base p-2 border-b-2 ${activeMobileTab === tab.value
              ? "border-brand_main text-brand_main"
              : "text-gray-500 border-gray-400 cursor-pointer"
              }`}
            onClick={() => setActiveMobileTab(tab.value)}
          >
            {tab.label}
          </li>
        ))}
      </ul>
      <div
        className={`${activeMobileTab !== "content" && "hidden"
          } lg:block space-y-12 relative`}
      >
        <div className="">
          {props.isStephanePlazaAccount && showInfoMessage ? (
            <div className="mx-auto text-brand_black font-medium bg-surface_50 border border-surface_300 py-5 px-12 my-5 rounded-lg text-center">
              <button
                type="button"
                onClick={() => setShowInfoMessage(false)}
                className="absolute top-6 right-1 p-1 bg-white rounded-md hover:bg-gray-100 group"
              >
                <XMarkIcon className="size-6 text-gray-700 group-hover:text-gray-500" />
              </button>
              {intl.formatMessage({ id: "publisher.ai_disclaimer", defaultMessage: "Les utilisateurs sont invités à vérifier les informations avant de prendre la décision de publier sur les réseaux sociaux. L’IA ne remplace pas l’humain. L’authenticité et la vérification des sources restent essentielles." })}
            </div>
          ) : null}

          <div className="hidden z-[5] lg:flex -mb-1 gap-1 relative border-limited w-full overflow-x-auto">
            {sortedBodyKeys?.map((bPid) => {
              const active = isActive(bPid);
              return (
                <div
                  key={`btn-${bPid}`}
                  className={`z-0 p-2 px-3 rounded-t-md border-t border-l border-r ${active ? "bg-white " : "bg-gray-100 cursor-pointer"
                    }`}
                  onClick={() => setDesktopActiveBody(bPid)}
                >
                  <ChannelAccountsUiElement
                    bPid={bPid}
                    post={post}
                    hChannelAccounts={hChannelAccounts}
                  />{" "}
                </div>
              );
            })}
          </div>
          <div className="flex flex-col gap-12">
            {sortedBodyKeys?.map((bPid, index) => (
              <BodyEditor
                key={bPid}
                index={index}
                body={hBodies[bPid]}
                aiImageEnabled={props.ai_image_enabled}
                isLast={sortedBodyKeys.length <= 1}
                isActive={isActive(bPid)} // setDesktopActiveBody
                setHBodies={desyncedSetHBodies}
                onRemoval={onBodyRemoval}
                company={props.company}
                onDuplication={onBodyDuplication}
                targetsSelectorRenderer={memoizedSelectorRenderer}
                defaultPictureSearch={props.industry}
                isStephanePlazaAccount={props.isStephanePlazaAccount}
                mentionSelectorRenderer={memoMentionSelectorRenderer}
                mentions={
                  post.post_on_channels_attributes[0]?.channel_specific_data
                    ?.mentions
                }
                variables={props.variables}
                characterLimit={characterLimitByBody[bPid]}
                hChannelAccounts={hChannelAccounts}
                sharedWithCount={sharedCompositionCount(hBodies, hBodies[bPid])}
              />
            ))}
          </div>
        </div>
        <div></div>
      </div>
      <div
        className={`${activeMobileTab !== "publication" && "hidden"} lg:block`}
      >
        <PublicationManager
          post={post}
          setPost={setPost}
          validationObject={validationObject}
          hChannelAccounts={hChannelAccounts}
          hBodies={hBodies}
          variables={props.variables}
          //setIsErrored={setIsErrored}
          openModalForPoc={openModalForPoc}
          setOpenModalForPoc={setOpenModalForPoc}
        />
      </div>
      <div className="fixed flex flex-col gap-3 h-fit bottom-6 right-8">
        <ErrorDisplayer validationObject={validationObject} />
      </div>
      {/* Hidden inputs */}
      <HiddenInput name="post[periodicity]" value={post.periodicity} />
      <HiddenInput name="post[scheduled_at]" value={post.scheduled_at} />
      <HiddenInput name="post[instant_publish]" value={post.instant_publish} />
      <HiddenInput name="post[draft]" value={post.draft ? 1 : 0} />
      <HiddenInput name="post[synced_with_source]" value={post.synced_with_source ? 1 : 0} />
      {props.hosting?.available && (
        <HiddenInput
          name="post[hosting_body_provisional_id]"
          value={hostedBodyPid !== null && hBodies[hostedBodyPid] ? hostedBodyPid : ""}
        />
      )}
      <PostOnChannelInputs
        originalPocsByCa={originalPocsByCa}
        postOnChannels={post.post_on_channels_attributes}
      />
    </div>
  );
};

// Ajoute les inputs pour créer, modifier et détruire les pocs
const PostOnChannelInputs = memo(
  ({
    originalPocsByCa,
    postOnChannels,
  }: {
    originalPocsByCa: Record<number, number>;
    postOnChannels: ProvisionalPost["post_on_channels_attributes"];
  }) => {
    const prefix = "post[post_on_channels_attributes]";
    const channelAccountIds = postOnChannels.map(
      (poc) => poc.channel_account_id
    );
    const destIndex = postOnChannels.length + 1;
    const destroyables = Object.entries(originalPocsByCa).filter(
      ([ca, _]) => !channelAccountIds.includes(parseInt(ca))
    );

    return (
      <>
        {postOnChannels.map(
          (
            {
              channel_account_id: caId,
              body_provisional_id: bPid,
              forced_scheduled_at,
              channel_specific_data,
              status,
            },
            i
          ) => (
            <div key={caId}>
              <HiddenInput
                name={`${prefix}[${i}][id]`}
                value={originalPocsByCa[caId]}
              />
              <HiddenInput
                name={`${prefix}[${i}][channel_account_id]`}
                value={caId}
              />
              <HiddenInput
                name={`${prefix}[${i}][body_provisional_id]`}
                value={bPid}
              />
              <HiddenInput
                name={`${prefix}[${i}][forced_scheduled_at]`}
                value={forced_scheduled_at}
              />
              <HiddenInput
                name={`${prefix}[${i}][status]`}
                value={status || "scheduled"}
              />

              {channel_specific_data && (
                <NestedDataInputs
                  data={channel_specific_data}
                  prefix={`${prefix}[${i}][channel_specific_data]`}
                />
              )}
            </div>
          )
        )}
        {destroyables.map(([c, pid], i) => (
          <div key={c}>
            <HiddenInput name={`${prefix}[${i + destIndex}][id]`} value={pid} />
            <HiddenInput
              name={`${prefix}[${i + destIndex}][_destroy]`}
              value={1}
            />
          </div>
        ))}
      </>
    );
  }
);

// Recursive component to handle nested data structures
const NestedDataInputs = memo(
  ({ data, prefix }: { data: any; prefix: string }) => {
    if (data === null || data === undefined) return null;

    return (
      <>
        {Object.entries(data).map(([key, value]) => {
          if (Array.isArray(value)) {
            return value.map((val, idx) => {
              if (typeof val === "object" && val !== null) {
                return (
                  <NestedDataInputs
                    key={`${key}_${idx}`}
                    data={val}
                    prefix={`${prefix}[${key}][]`}
                  />
                );
              }
              return (
                <HiddenInput
                  key={`${key}_${idx}`}
                  name={`${prefix}[${key}][]`}
                  value={val}
                />
              );
            });
          } else if (typeof value === "object" && value !== null) {
            return (
              <NestedDataInputs
                key={key}
                data={value}
                prefix={`${prefix}[${key}]`}
              />
            );
          }
          return (
            <HiddenInput key={key} name={`${prefix}[${key}]`} value={value} />
          );
        })}
      </>
    );
  }
);

export default PostStateManager;
